import React, { useState, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  FileSpreadsheet,
  Sparkles,
  Trash2,
  Eye,
  ChevronUp
} from 'lucide-react';

// Import WorksheetMapping from shared schemas
// Note: We're using a local type definition that matches the shared schema
interface WorksheetMapping {
  upfront?: string;
  trail?: string;
  nil?: string;
  summary?: string;
}

interface DetectedSheet {
  name: string;
  rowCount: number;
  headers: string[];
}

interface WorksheetMapperProps {
  detectedSheets: DetectedSheet[];
  worksheetMapping: WorksheetMapping;
  onChange: (mapping: WorksheetMapping) => void;
  suggestions?: WorksheetMapping; // Auto-detected suggestions
}

type WorksheetTypeKey = 'upfront' | 'trail' | 'nil' | 'summary';

const WORKSHEET_TYPES: Array<{
  key: WorksheetTypeKey;
  label: string;
  color: string;
  description: string;
  required: boolean;
}> = [
  {
    key: 'upfront',
    label: 'Upfront Commissions',
    color: 'bg-green-500/20 text-green-400 border-green-500/30',
    description: 'Initial commissions paid on new loan settlements',
    required: true
  },
  {
    key: 'trail',
    label: 'Trail Commissions',
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    description: 'Ongoing recurring commissions on active loans',
    required: true
  },
  {
    key: 'nil',
    label: 'Nil Payments',
    color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    description: 'Zero-value trail payments (loan still active)',
    required: false
  },
  {
    key: 'summary',
    label: 'Summary/Totals',
    color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    description: 'Summary sheet with totals and reconciliation',
    required: false
  }
];

const WorksheetMapper: React.FC<WorksheetMapperProps> = ({
  detectedSheets,
  worksheetMapping,
  onChange,
  suggestions
}) => {
  const [expandedPreviews, setExpandedPreviews] = useState<Set<string>>(new Set());

  // Toggle preview expansion
  const togglePreview = (sheetName: string) => {
    setExpandedPreviews(prev => {
      const next = new Set(prev);
      if (next.has(sheetName)) {
        next.delete(sheetName);
      } else {
        next.add(sheetName);
      }
      return next;
    });
  };

  // Handle mapping change
  const handleMappingChange = (type: WorksheetTypeKey, sheetName: string | undefined) => {
    const newMapping = { ...worksheetMapping };

    if (!sheetName || sheetName === '') {
      delete newMapping[type];
    } else {
      newMapping[type] = sheetName;
    }

    onChange(newMapping);
  };

  // Clear specific mapping
  const clearMapping = (type: WorksheetTypeKey) => {
    const newMapping = { ...worksheetMapping };
    delete newMapping[type];
    onChange(newMapping);
  };

  // Apply auto-detected suggestions
  const applySuggestions = () => {
    if (suggestions) {
      onChange({ ...suggestions });
    }
  };

  // Calculate confidence score for suggestions
  const suggestionConfidence = useMemo(() => {
    if (!suggestions) return 0;

    let matches = 0;
    let total = 0;

    WORKSHEET_TYPES.forEach(({ key }) => {
      if (suggestions[key]) {
        total++;
        const suggested = suggestions[key];
        const exists = detectedSheets.some(s => s.name === suggested);
        if (exists) matches++;
      }
    });

    return total > 0 ? (matches / total) * 100 : 0;
  }, [suggestions, detectedSheets]);

  // Check for duplicate mappings
  const duplicateWarning = useMemo(() => {
    const usedSheets = new Set<string>();
    const duplicates = new Set<string>();

    Object.values(worksheetMapping).forEach(sheetName => {
      if (sheetName) {
        if (usedSheets.has(sheetName)) {
          duplicates.add(sheetName);
        }
        usedSheets.add(sheetName);
      }
    });

    return duplicates.size > 0 ? Array.from(duplicates) : null;
  }, [worksheetMapping]);

  // Mapping status
  const mappingStatus = useMemo(() => {
    const requiredTypes = WORKSHEET_TYPES.filter(t => t.required);
    const mappedRequired = requiredTypes.filter(t => worksheetMapping[t.key]);
    const mappedOptional = WORKSHEET_TYPES.filter(t => !t.required && worksheetMapping[t.key]);

    return {
      requiredMapped: mappedRequired.length,
      requiredTotal: requiredTypes.length,
      optionalMapped: mappedOptional.length,
      optionalTotal: WORKSHEET_TYPES.filter(t => !t.required).length,
      isComplete: mappedRequired.length === requiredTypes.length
    };
  }, [worksheetMapping]);

  // Get sheet that is currently mapped (for checking duplicates)
  const getMappedType = (sheetName: string): WorksheetTypeKey | null => {
    for (const [key, value] of Object.entries(worksheetMapping)) {
      if (value === sheetName) {
        return key as WorksheetTypeKey;
      }
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium text-lg">Worksheet Mapping</h3>
          <p className="text-slate-400 text-sm mt-1">
            Map worksheets to commission types for multi-sheet Excel files
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              mappingStatus.isComplete ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              Required: {mappingStatus.requiredMapped}/{mappingStatus.requiredTotal}
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
              Optional: {mappingStatus.optionalMapped}/{mappingStatus.optionalTotal}
            </span>
          </div>

          {suggestions && (
            <button
              onClick={applySuggestions}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
            >
              <Sparkles className="w-4 h-4" />
              Apply Suggestions ({suggestionConfidence.toFixed(0)}% confidence)
            </button>
          )}
        </div>
      </div>

      {/* Validation Warnings */}
      {!mappingStatus.isComplete && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-red-400 font-medium">Missing Required Mappings</h4>
              <p className="text-slate-400 text-sm mt-1">
                {mappingStatus.requiredTotal - mappingStatus.requiredMapped} required worksheet type{mappingStatus.requiredTotal - mappingStatus.requiredMapped > 1 ? 's' : ''} must be mapped.
              </p>
            </div>
          </div>
        </div>
      )}

      {duplicateWarning && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-400 font-medium">Duplicate Mappings Detected</h4>
              <p className="text-slate-400 text-sm mt-1">
                The following sheets are mapped to multiple types: {duplicateWarning.join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Detected Sheets Overview */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-blue-400 font-medium">
              Detected {detectedSheets.length} worksheet{detectedSheets.length !== 1 ? 's' : ''}
            </h4>
            <p className="text-slate-400 text-sm mt-1">
              Sheets: {detectedSheets.map(s => s.name).join(', ')}
            </p>
          </div>
        </div>
      </div>

      {/* Worksheet Type Mappings */}
      <div className="space-y-4">
        {WORKSHEET_TYPES.map(type => {
          const currentMapping = worksheetMapping[type.key];
          const suggestion = suggestions?.[type.key];
          const isMapped = !!currentMapping;
          const hasSuggestion = !!suggestion;

          return (
            <div key={type.key} className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
              <div className="flex items-start gap-4">
                {/* Type Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center px-3 py-1 rounded border text-sm font-medium ${type.color}`}>
                      {type.label}
                    </span>
                    {type.required && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-slate-400 text-sm">{type.description}</p>
                </div>

                {/* Mapping Selector */}
                <div className="flex items-center gap-2 w-80">
                  <select
                    value={currentMapping || ''}
                    onChange={e => handleMappingChange(type.key, e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-800/50 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select worksheet</option>
                    {detectedSheets.map(sheet => {
                      const alreadyMappedTo = getMappedType(sheet.name);
                      const isDuplicate = alreadyMappedTo && alreadyMappedTo !== type.key;

                      return (
                        <option
                          key={sheet.name}
                          value={sheet.name}
                          disabled={isDuplicate || undefined}
                        >
                          {sheet.name} ({sheet.rowCount} rows)
                          {isDuplicate ? ` - Already mapped to ${alreadyMappedTo}` : ''}
                        </option>
                      );
                    })}
                  </select>

                  {/* Clear Button */}
                  {isMapped && (
                    <button
                      onClick={() => clearMapping(type.key)}
                      className="p-2 bg-slate-700/50 hover:bg-slate-700 text-slate-400 hover:text-red-400 rounded transition-colors"
                      title="Clear mapping"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}

                  {/* Status Icon */}
                  <div className="w-6 flex items-center justify-center">
                    {isMapped ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : type.required ? (
                      <XCircle className="w-5 h-5 text-red-400" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Auto-suggestion hint */}
              {hasSuggestion && !isMapped && (
                <div className="mt-3 pl-4 border-l-2 border-purple-500/30">
                  <p className="text-purple-400 text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Suggested: "{suggestion}"
                  </p>
                </div>
              )}

              {/* Sheet Preview */}
              {isMapped && currentMapping && (
                <div className="mt-3 border-t border-slate-700/50 pt-3">
                  <button
                    onClick={() => togglePreview(currentMapping)}
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition-colors"
                  >
                    {expandedPreviews.has(currentMapping) ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Hide Preview
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Show Preview
                      </>
                    )}
                  </button>

                  {expandedPreviews.has(currentMapping) && (() => {
                    const sheet = detectedSheets.find(s => s.name === currentMapping);
                    if (!sheet) return null;

                    return (
                      <div className="mt-3 bg-slate-800/50 rounded-lg overflow-hidden">
                        <div className="px-3 py-2 bg-slate-700/50 border-b border-slate-600/50">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-400">
                              {sheet.headers.length} columns
                            </span>
                            <span className="text-slate-400">
                              {sheet.rowCount} rows
                            </span>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <div className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              {sheet.headers.slice(0, 10).map((header, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs rounded"
                                >
                                  {header}
                                </span>
                              ))}
                              {sheet.headers.length > 10 && (
                                <span className="px-2 py-1 text-slate-500 text-xs">
                                  +{sheet.headers.length - 10} more
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-blue-400 font-medium">Worksheet Mapping Tips</h4>
            <ul className="text-slate-400 text-sm mt-2 space-y-1">
              <li>• Map upfront and trail worksheets for complete commission tracking</li>
              <li>• Nil payment worksheets record zero-value trails (optional but recommended)</li>
              <li>• Summary worksheets are used for validation and reconciliation</li>
              <li>• Each worksheet can only be mapped to one type</li>
              <li>• Auto-suggestions use sheet name pattern matching (upfront, trail, nil, summary)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorksheetMapper;
